// app/javascript/controllers/coupon_controller.js
import { Controller } from "@hotwired/stimulus";

function readI18n(scope, key, fallback) {
  try {
    const data = JSON.parse(document.getElementById("i18n-js")?.textContent || "{}");
    return data?.[scope]?.[key] || fallback;
  } catch {
    return fallback;
  }
}

export default class extends Controller {
  static values = { offerId: String, url: String };

  async getCoupon(event) {
    event.preventDefault();
    const button = event.currentTarget;
    button.disabled = true;
    button.classList.add("opacity-70");

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
          Accept: "application/json",
        },
        body: JSON.stringify({ offer_id: this.offerIdValue }),
      });

      const data = await response.json();

      if (!response.ok) {
        this.showError(data.error || readI18n("coupon", "network_error", "Failed"));
        return;
      }

      this.showQrModal(data);
    } catch (error) {
      this.showError(readI18n("coupon", "network_error", "Network error"));
    } finally {
      button.disabled = false;
      button.classList.remove("opacity-70");
    }
  }

  showQrModal(coupon) {
    const modals = document.getElementById("modals");
    if (!modals) return;

    const locale = document.documentElement.lang || "es";
    const t = (key, fallback) => readI18n("coupon", key, fallback);
    const qrImg = coupon.qr_code
      ? `<img src="${coupon.qr_code}" alt="QR" class="h-52 w-52 rounded-xl border-4 border-brand-100 p-2 mx-auto dark:border-brand-800">`
      : `<p class="text-red-500 text-center">${t("qr_unavailable", "QR unavailable")}</p>`;

    const clinicLink = coupon.clinic_path
      ? `<a href="${coupon.clinic_path}" class="mt-3 inline-block text-sm font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400">${coupon.clinic_name || t("clinic_fallback", "Clinic")} →</a>`
      : "";

    modals.innerHTML = `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" id="coupon-qr-modal" role="dialog" aria-modal="true" aria-labelledby="coupon-modal-title">
        <div class="w-full max-w-sm rounded-2xl border border-ink-200 bg-white p-6 shadow-2xl dark:border-ink-700 dark:bg-ink-900">
          <h2 id="coupon-modal-title" class="text-center text-xl font-bold text-ink-900 dark:text-white">${t("ready", "Coupon ready!")}</h2>
          <p class="mt-1 text-center text-sm text-ink-500 dark:text-ink-400">${t("subtitle", "Show this QR at the clinic")}</p>
          ${coupon.offer_title ? `<p class="mt-2 text-center text-sm font-semibold text-ink-700 dark:text-ink-200">${coupon.offer_title}</p>` : ""}
          <div class="mt-6 flex justify-center">${qrImg}</div>
          <p class="mt-4 text-center text-sm text-ink-600 dark:text-ink-300">${t("code_label", "Code")}: <strong class="font-mono text-lg text-ink-900 dark:text-white">${coupon.code}</strong></p>
          <div class="text-center">${clinicLink}</div>
          <div class="mt-6 flex gap-2">
            <button type="button" class="btn-secondary flex-1" data-action="coupon#closeModal">${t("close", "Close")}</button>
            <a href="/${locale}/coupons" class="btn-primary flex-1 text-center font-extrabold">${t("my_coupons", "My coupons")}</a>
          </div>
        </div>
      </div>
    `;
  }

  closeModal() {
    const modals = document.getElementById("modals");
    if (modals) modals.innerHTML = "";
  }

  showError(message) {
    const modals = document.getElementById("modals");
    const closeLabel = readI18n("coupon", "close", "Close");
    if (!modals) {
      alert(message);
      return;
    }
    modals.innerHTML = `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="alertdialog" aria-modal="true">
        <div class="w-full max-w-sm rounded-2xl border border-ink-200 bg-white p-6 shadow-2xl dark:border-ink-700 dark:bg-ink-900">
          <p class="text-red-600 text-center dark:text-red-400">${message}</p>
          <button type="button" class="btn-secondary mt-4 w-full" data-action="coupon#closeModal">${closeLabel}</button>
        </div>
      </div>
    `;
  }
}
